import { useQuery, useMutation } from '@tanstack/react-query'
import mixpanel from 'mixpanel-browser'
import React, { useContext, createContext, useState, useCallback, type JSX } from 'react'
import { useSessionStorage } from 'react-use'

import { Context, getContexts, putSelectContext } from 'api/contexts'
import { getCapabilities } from 'api/login'
import { AUTH_TOKENS, CLIENT_ID, MFA_TOKENS, CONTEXT_TOKEN, SEAT_ID } from 'constants/web-storage'
import Sentry from 'integrations/Sentry'

type UserContextProps = {
  token: string
  setToken(token: string): any
  mfaToken: string
  setMfaToken(mfaToken: string): any
  contexts: Context[]
  context: Context
  setContext(context: Context): any
  setClientId(id: string): any
  clientId: string
  seatId: string
  capabilities?: string[]
  logout(): void
}

export const UserContext = createContext<Partial<UserContextProps>>({})

UserContext.displayName = 'UserContext'

export function useUserContext(): any {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider')
  }
  return context
}

type UserProviderProps = {
  children?: React.ReactNode
}

export function formatFullName(context: Context): string {
  return `${context.firstName} ${context.lastName}`.trim()
}

export function UserProvider({ children }: UserProviderProps): JSX.Element {
  const [token, setToken] = useSessionStorage<string>(AUTH_TOKENS, '', true)
  const [mfaToken, setMfaToken] = useSessionStorage<string>(MFA_TOKENS, '', true)
  const [clientId, setClientId] = useSessionStorage<string>(CLIENT_ID, '', true)
  const [seatId, setSeatId] = useSessionStorage<string>(SEAT_ID, '', true)
  const [context, setStateContext] = useState<Context | undefined>()
  const [contexts, setContexts] = useState<Context[] | undefined>()

  const resetSentry = useCallback(() => {
    Sentry.setContext('client', null)
    Sentry.setTags({
      'client.id': null,
    })
  }, [])

  const { mutate: saveContext } = useMutation(putSelectContext, {
    onSuccess(data) {
      setSeatId(data.data.seatId)
      sessionStorage.setItem(CONTEXT_TOKEN, data.jwt)
    },
  })

  const setContext = async (context?: Context): Promise<void> => {
    if (context !== undefined) {
      saveContext({ context: context.clientId })
      setClientId(context.clientId)
      setStateContext(context)
    }
  }

  useQuery(['contexts', token], () => getContexts(token), {
    enabled: !!token,
    onSuccess(data) {
      setContexts(data)

      if (!clientId && data.length > 1) {
        return
      }

      const currentContext = data.find((i) => i.clientId === clientId) || data.find((i) => i.currentContext)

      let id = clientId
      if (currentContext) {
        setClientId(currentContext.clientId)
        id = currentContext.clientId

        Sentry.setContext('client', {
          client_id: currentContext?.clientId,
        })

        Sentry.setTags({
          'client.id': currentContext?.clientId,
          'client.name': formatFullName(currentContext),
        })
      }

      sessionStorage.setItem(CLIENT_ID, id || '')
      setContext(currentContext)
    },
    onError(err: any) {
      Sentry.captureException(err)

      if (err.status === 401) {
        setToken('')
      }

      resetSentry()
    },
  })

  const { data: capabilities } = useQuery(
    ['capabilities', { clientId, seatId, token }],
    () => getCapabilities({ clientId, seatId, token }),
    {
      initialData: [],
      onSuccess(data) {
        Sentry.setContext('capabilities', data)
      },
      onError(err: any) {
        Sentry.captureException(err)

        Sentry.setContext('capabilities', null)
      },
      enabled: Boolean(seatId) && Boolean(clientId),
    },
  )

  const logout = (): void => {
    setToken('')
    setClientId('')
    setStateContext(undefined)
    sessionStorage.removeItem(AUTH_TOKENS)
    sessionStorage.removeItem(CLIENT_ID)
    mixpanel.reset()
    resetSentry()
  }

  return (
    <UserContext.Provider
      value={{
        token,
        setToken,
        mfaToken,
        setMfaToken,
        contexts,
        context,
        setContext,
        setClientId,
        clientId,
        seatId,
        logout,
        capabilities,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
